"use client";

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ChevronDown } from "lucide-react";

/**
 * Shows the first cards of a grid in full and the next row cut in half under a
 * fade, with a button to open the rest. `children` is the <ul> of cards (it
 * stays a server component). The cut is measured from the real cards, so it
 * is right for one, two or three columns. Without JavaScript everything shows.
 */
export function RevealMore({
  children,
  shown = 3,
  more,
  less = "Show fewer",
}: {
  children: ReactNode;
  /** how many cards to show in full before the cut */
  shown?: number;
  /** button text while closed, e.g. "See all 12 features" */
  more: string;
  less?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [cut, setCut] = useState<number | null>(null); // null: not measured yet, show everything
  const [full, setFull] = useState<number>(0);
  const [fade, setFade] = useState<number>(0);
  const [settled, setSettled] = useState(false); // open and finished growing: stop clipping the bottom
  const [open, setOpen] = useState(false);
  const [still, setStill] = useState(false);
  const id = useId();

  const measure = useCallback(() => {
    const list = box.current?.querySelector("ul");
    const items = list ? (Array.from(list.children) as HTMLElement[]) : [];
    if (!list || items.length <= shown) return setCut(null);
    const top = box.current!.getBoundingClientRect().top;
    const next = items[shown].getBoundingClientRect();
    const listH = list.getBoundingClientRect().height;
    // Not laid out (hidden, or the browser skipped it): keep the last good numbers.
    // Setting them from zeros makes the box resize, which re-measures, and so on.
    if (next.height < 1 || listH < 1) return;
    // all of the cards before `shown`, then half of the next row
    const c = Math.round(next.top - top + next.height * 0.5);
    const f = Math.round(next.height * 0.36);
    const h = Math.round(listH);
    // only update on a real change (≥2px), so a 1px rounding wobble can't loop
    setCut((o) => (o !== null && Math.abs(o - c) < 2 ? o : c));
    setFade((o) => (Math.abs(o - f) < 2 ? o : f));
    setFull((o) => (Math.abs(o - h) < 2 ? o : h));
  }, [shown]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    setStill(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    const list = box.current?.querySelector("ul");
    if (!list || typeof ResizeObserver === "undefined") return;
    // one measure per frame at most, whatever the observer reports
    let frame = 0;
    const ro = new ResizeObserver(() => {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; measure(); });
    });
    ro.observe(list);
    return () => {
      ro.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [measure]);

  // Cards hidden behind the fade can't be reached with the keyboard until opened.
  useEffect(() => {
    const items = box.current?.querySelector("ul")?.children;
    if (!items) return;
    (Array.from(items) as Element[]).forEach((el, i) => {
      if (cut !== null && !open && i >= shown) el.setAttribute("inert", "");
      else el.removeAttribute("inert");
    });
  }, [cut, open, shown]);

  function toggle() {
    if (open) {
      // closing: bring the section's top back into view so the page doesn't jump away
      const top = box.current?.getBoundingClientRect().top ?? 0;
      if (top < 0)
        box.current?.scrollIntoView({
          behavior: still ? "auto" : "smooth",
          block: "start",
        });
    }
    setSettled(still && !open); // without animation there is no transition to wait for
    setOpen((o) => !o);
  }

  const collapsed = cut !== null && !open;
  return (
    <div>
      <div
        ref={box}
        id={id}
        className="relative"
        onTransitionEnd={(e) => {
          if (e.target === box.current && open) setSettled(true);
        }}
        style={{
          maxHeight:
            cut === null ? undefined : open ? (settled ? "none" : full) : cut,
          // Only the bottom edge is cut: card shadows stay visible on the top and sides
          // (overflow: hidden would clip them).
          clipPath:
            cut === null || settled ? undefined : "inset(-80px -80px 0 -80px)",
          transition: still
            ? undefined
            : "max-height .7s cubic-bezier(.19,1,.22,1)",
        }}
      >
        {children}
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-x-[-80px] bottom-0 bg-gradient-to-b from-[#faf9fd]/0 to-[#faf9fd] transition-opacity duration-500 ${collapsed ? "opacity-100" : "opacity-0"}`}
          style={{ height: fade }}
        />
      </div>
      {cut !== null && (
        <div
          className={`relative z-10 flex justify-center ${collapsed ? "-mt-5" : "mt-8"}`}
        >
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={id}
            className="group inline-flex h-12 items-center gap-2 rounded-full bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep px-7 text-[0.95rem] font-extrabold text-white shadow-[0_18px_38px_-14px_rgba(143,103,255,0.85),inset_0_1px_0_rgba(255,255,255,0.3)] transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet/30"
          >
            {open ? less : more}
            <ChevronDown
              size={18}
              aria-hidden
              className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      )}
    </div>
  );
}
